import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import { useRecordingCountdown } from './useRecordingCountdown';
import { validateAudioFile, validateAudioDuration, validateFileCount } from './validators';
import { AUDIO_EXTENSIONS } from '../../utils/audioFile';
import { AudioInputError, AudioInputFile, AudioInputProps } from './AudioInput.types';

export interface MicDevice {
    deviceId: string;
    label: string;
}

export interface AudioInputController {
    files: AudioInputFile[];
    isMultiple: boolean;
    isDragOver: boolean;
    isRecording: boolean;
    isStartingRecording: boolean;
    isRecordingPanelOpen: boolean;
    countdown: number | null;
    recordingDuration: number;
    error: AudioInputError | null;
    micDevices: MicDevice[];
    selectedMicDeviceId: string;
    setSelectedMicDeviceId: (id: string) => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    openFilePicker: () => void;
    openRecorder: () => void;
    closeRecorder: () => void;
    handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleDrop: (e: React.DragEvent) => void;
    handleDragOver: (e: React.DragEvent) => void;
    handleDragLeave: () => void;
    clearFile: (file?: AudioInputFile) => void;
    startCountdownThenRecord: () => void;
    stopRecording: () => void;
    reportDuration: (file: AudioInputFile, durationSec: number) => void;
}

// Headless controller: validation, file state, recorder wiring, drag/drop, mic enumeration.
export function useAudioInputController(props: AudioInputProps): AudioInputController {
    const { multiple = false, maxFiles = 1, value, onChange, validation = {}, recording = {}, onError } = props;

    const {
        allowRecording = true,
        micDeviceSelector = false,
        maxRecordingDurationSec = 30,
        countdownSec = 3,
    } = recording;

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isRecordingPanelOpen, setIsRecordingPanelOpen] = useState(false);
    const [error, setError] = useState<AudioInputError | null>(null);
    const [micDevices, setMicDevices] = useState<MicDevice[]>([]);
    const [selectedMicDeviceId, setSelectedMicDeviceId] = useState('');

    const files: AudioInputFile[] = useMemo(() => {
        if (!value) return [];
        return Array.isArray(value) ? value : [value];
    }, [value]);

    // Revoke leftover object URLs if we unmount before the file is submitted/cleared.
    const filesRef = useRef(files);
    filesRef.current = files;
    useEffect(() => {
        return () => {
            filesRef.current.forEach(f => URL.revokeObjectURL(f.previewUrl));
        };
    }, []);

    const raiseError = useCallback((err: AudioInputError) => {
        setError(err);
        onError?.(err);
    }, [onError]);

    const commitValue = useCallback((next: AudioInputFile[]) => {
        onChange(multiple ? next : (next[0] ?? null));
    }, [multiple, onChange]);

    const selectFile = useCallback((file: File) => {
        if (multiple) {
            const countErr = validateFileCount(files.length, 1, maxFiles);
            if (countErr) { raiseError(countErr); return; }
        }

        const fileErr = validateAudioFile(file, {
            maxSizeBytes: validation.maxSizeBytes,
            allowedExtensions: validation.allowedExtensions ?? AUDIO_EXTENSIONS,
            allowedMimePrefixes: validation.allowedMimePrefixes,
        });
        if (fileErr) { raiseError(fileErr); return; }

        setError(null);
        const previewUrl = URL.createObjectURL(file);
        const next: AudioInputFile = { file, previewUrl };

        if (multiple) {
            commitValue([...files, next]);
        } else {
            files.forEach(f => URL.revokeObjectURL(f.previewUrl));
            commitValue([next]);
        }
        setIsRecordingPanelOpen(false);
    }, [files, multiple, maxFiles, validation, raiseError, commitValue]);

    const clearFile = useCallback((target?: AudioInputFile) => {
        if (multiple && target) {
            URL.revokeObjectURL(target.previewUrl);
            commitValue(files.filter(f => f !== target));
        } else {
            files.forEach(f => URL.revokeObjectURL(f.previewUrl));
            commitValue([]);
        }
        setError(null);
    }, [files, multiple, commitValue]);

    const reportDuration = useCallback((target: AudioInputFile, durationSec: number) => {
        const durErr = validateAudioDuration(durationSec, {
            minDurationSec: validation.minDurationSec,
            maxDurationSec: validation.maxDurationSec,
        });
        if (durErr) {
            raiseError(durErr);
            // Same as selectFile: reject the file instead of keeping a bad one selected.
            URL.revokeObjectURL(target.previewUrl);
            commitValue(files.filter(f => f !== target));
            return;
        }

        commitValue(files.map(f => (f === target ? { ...f, durationSec } : f)));
    }, [files, validation.minDurationSec, validation.maxDurationSec, raiseError, commitValue]);

    const { isRecording, duration: recordingDuration, startRecording, stopRecording, cancelRecording } = useAudioRecorder({
        maxDuration: maxRecordingDurationSec,
        deviceId: micDeviceSelector ? (selectedMicDeviceId || undefined) : undefined,
        onRecordingComplete: (blob) => {
            const recorded = new File([blob], 'recording.wav', { type: 'audio/wav' });
            selectFile(recorded);
        },
    });

    // Bridges the gap between countdown-ends and isRecording-flips-true (startRecording awaits getUserMedia).
    const [isStartingRecording, setIsStartingRecording] = useState(false);
    const beginRecording = useCallback(async () => {
        setIsStartingRecording(true);
        try {
            await startRecording();
        } finally {
            setIsStartingRecording(false);
        }
    }, [startRecording]);

    const { countdown, start: startCountdown, cancel: cancelCountdown } = useRecordingCountdown(
        beginRecording,
        countdownSec
    );

    const openFilePicker = useCallback(() => fileInputRef.current?.click(), []);

    const openRecorder = useCallback(() => {
        if (!allowRecording) return;
        setError(null);
        setIsRecordingPanelOpen(true);
    }, [allowRecording]);

    // Clear any prior error (e.g. "too short") when the user starts a fresh recording.
    const startCountdownThenRecord = useCallback(() => {
        setError(null);
        startCountdown();
    }, [startCountdown]);

    const closeRecorder = useCallback(() => {
        if (isRecording) cancelRecording();
        cancelCountdown();
        setIsRecordingPanelOpen(false);
    }, [isRecording, cancelRecording, cancelCountdown]);

    const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) selectFile(f);
        e.target.value = '';
    }, [selectFile]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) selectFile(f);
    }, [selectFile]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => setIsDragOver(false), []);

    // Briefly open a stream to read device labels, then release it right away.
    useEffect(() => {
        if (!micDeviceSelector || !isRecordingPanelOpen) return;
        let cancelled = false;

        (async () => {
            try {
                const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                permissionStream.getTracks().forEach(track => track.stop());
                const devices = await navigator.mediaDevices.enumerateDevices();
                if (cancelled) return;
                const audioDevices = devices
                    .filter(d => d.kind === 'audioinput')
                    .map(d => ({ deviceId: d.deviceId, label: d.label || `Microphone ${d.deviceId.slice(0, 5)}` }));
                setMicDevices(audioDevices);
                if (audioDevices.length > 0) {
                    const defaultMic = audioDevices.find(m => m.deviceId === 'default') || audioDevices[0];
                    setSelectedMicDeviceId(defaultMic.deviceId);
                }
            } catch (e) {
                console.error('Error accessing microphones:', e);
                raiseError({ code: 'mic-permission-denied', message: 'Microphone access was denied.' });
            }
        })();

        return () => { cancelled = true; };
    }, [micDeviceSelector, isRecordingPanelOpen, raiseError]);

    return {
        files,
        isMultiple: multiple,
        isDragOver,
        isRecording,
        isStartingRecording,
        isRecordingPanelOpen,
        countdown,
        recordingDuration,
        error,
        micDevices,
        selectedMicDeviceId,
        setSelectedMicDeviceId,
        fileInputRef,
        openFilePicker,
        openRecorder,
        closeRecorder,
        handleFileInputChange,
        handleDrop,
        handleDragOver,
        handleDragLeave,
        clearFile,
        startCountdownThenRecord,
        stopRecording,
        reportDuration,
    };
}
